import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Button, Card, FormGrid, SelectField } from '@crave/ui';
import { api, orderSiteUrl } from '../api.ts';
import { ConfirmDelete } from '../components/ConfirmDelete.tsx';
import { QrCode } from '../components/QrCode.tsx';
import { Notice, Section, Table, errorText, when } from '../components/ui.tsx';
import { useStaff } from '../lib/staff.tsx';

interface Recent {
  id: string;
  source: string;
  overall: number | null;
  would_order: string | null;
  comment: string | null;
  created_at: string;
  order_number: string | null;
  /** How each pizza on the order was rated, when the customer rated them one by one. */
  pizzas: string | null;
  first_name: string | null;
  email: string | null;
  contact_email: string | null;
  sample_code: string | null;
  can_delete?: boolean;
}

interface Report {
  days: number;
  summary: {
    source: string;
    responses: number;
    overall: number | null;
    crust: number | null;
    sauce: number | null;
    toppings: number | null;
    yes: number;
    maybe: number;
    no: number;
  }[];
  by_item: { name: string; responses: number; overall: number | null }[];
  recent: Recent[];
  response_rate: { completed: number; rated: number };
}

const score = (n: number | null) => (n === null ? 'None' : n.toFixed(1));

const againLabel = (v: string | null) =>
  v === 'YES' ? 'Yes' : v === 'MAYBE' ? 'Maybe' : v === 'NO' ? 'No' : '';

const SOURCE_WORDS: Record<string, string> = {
  order: 'Orders',
  tasting: 'Tastings',
  manual: 'Feedback link',
};
const sourceLabel = (source: string) => SOURCE_WORDS[source] ?? source;

const fromLabel = (f: Recent) => {
  if (f.order_number) return `${f.first_name ?? f.email ?? 'Customer'} · ${f.order_number}`;
  // Nobody signs in to use the feedback link, so there is only an email when they left one.
  if (f.source === 'manual')
    return `Feedback link${f.contact_email ? ` · ${f.contact_email}` : ''}`;
  return `Tasting ${f.sample_code ?? ''}${f.contact_email ? ` · ${f.contact_email}` : ''}`;
};

/** DELETE sends no body, so the optional reason travels as ?reason= (the API reads both). */
const withReason = (path: string, reason: string | undefined) =>
  reason ? `${path}?reason=${encodeURIComponent(reason)}` : path;

const PERIODS = [
  ['30', 'Last 30 days'],
  ['90', 'Last 90 days'],
  ['365', 'Last year'],
] as const;

interface TeamNote {
  id: string;
  kind: 'problem' | 'idea' | 'question';
  message: string;
  app: string;
  path: string | null;
  release: string | null;
  status: 'new' | 'planned' | 'done' | 'declined';
  note: string | null;
  created_at: string;
  location: string | null;
  sent_by: string | null;
  pos_number: number | null;
}

const KIND_WORDS: Record<string, string> = {
  problem: 'Problem',
  idea: 'Idea',
  question: 'Question',
};
/** A path is for us; the owner reads a screen name. Unknown paths become words, not a URL. */
const SCREENS: Record<string, string> = {
  'kitchen:/': 'Queue',
  'kitchen:/pos': 'New order',
  'admin:/': 'Dashboard',
};
const screenName = (app: string, path: string | null) => {
  if (!path) return null;
  const known = SCREENS[`${app}:${path}`];
  if (known) return known;
  const words = path.replace(/^\//, '').replace(/[/-]/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : null;
};

const DECISIONS: [TeamNote['status'], string][] = [
  ['new', 'New'],
  ['planned', 'Planned'],
  ['done', 'Done'],
  ['declined', 'Not doing'],
];

/** What the team said about Crave itself (#61), newest first, with what was decided. */
function FromTheTeam() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['staff-feedback'],
    queryFn: async () =>
      (
        await api.get<{ items: TeamNote[]; counts: Record<string, number> }>(
          '/admin/staff-feedback',
        )
      ).data,
    refetchInterval: 60_000,
  });
  const [msg, setMsg] = useState<string | null>(null);
  const decide = useMutation({
    mutationFn: async (v: { id: string; status: TeamNote['status'] }) =>
      api.patch(`/admin/staff-feedback/${v.id}`, { status: v.status }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['staff-feedback'] }),
    onError: (err) => setMsg(errorText(err)),
  });
  const items = list.data?.items ?? [];
  const isNew = list.data?.counts['new'] ?? 0;
  return (
    <Card>
      <strong>From the team</strong>
      <p style={{ margin: '4px 0 12px', color: 'var(--ink-soft)' }}>
        Sent from the Kitchen app and admin with the screen, the release and the POS attached.
        {isNew > 0 ? ` ${isNew} still to look at.` : ''}
      </p>
      {list.isError && (
        <Notice kind="error">Could not load team feedback. {errorText(list.error, '')}</Notice>
      )}
      {msg && <Notice kind="error">{msg}</Notice>}
      {list.isSuccess && items.length === 0 && (
        <p style={{ margin: 0, color: 'var(--ink-soft)' }}>Nothing yet.</p>
      )}
      <div style={{ display: 'grid' }}>
        {items.map((f, i) => (
          <div
            key={f.id}
            style={{
              borderTop: i ? '1px solid var(--line)' : 0,
              padding: '12px 0',
              display: 'grid',
              gap: 8,
            }}
          >
            <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <span className="crave-status" data-tone={f.kind === 'problem' ? 'alert' : 'muted'}>
                {KIND_WORDS[f.kind] ?? f.kind}
              </span>
              <strong style={{ flex: 1, minWidth: 240, fontWeight: 500 }}>{f.message}</strong>
            </div>
            <div style={{ fontSize: 14, color: 'var(--ink-soft)' }}>
              {[
                f.sent_by,
                when(f.created_at),
                f.app === 'kitchen' ? 'Kitchen app' : 'Admin',
                screenName(f.app, f.path),
                f.location,
                f.pos_number ? `POS ${f.pos_number}` : null,
                f.release ? `Crave ${f.release}` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {DECISIONS.map(([value, label]) => (
                <Button
                  key={value}
                  variant={f.status === value ? 'primary' : 'secondary'}
                  disabled={decide.isPending}
                  onClick={() => decide.mutate({ id: f.id, status: value })}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

/** The link and the QR to put on the counter, a box or a receipt: one page, no account needed. */
function FeedbackLink() {
  const url = `${orderSiteUrl}/feedback`;
  const [copied, setCopied] = useState(false);
  return (
    <Card>
      <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        <QrCode value={url} size={124} />
        <div style={{ flex: 1, minWidth: 260, display: 'grid', gap: 8 }}>
          <strong>The feedback link</strong>
          <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
            Put this QR on the counter or a box. Anyone can rate a pizza in twenty seconds, with or
            without an order number, and it lands below.
          </p>
          <code style={{ fontSize: 15, wordBreak: 'break-all' }}>{url}</code>
          <div>
            <Button
              variant="secondary"
              onClick={() => {
                void navigator.clipboard?.writeText(url).then(() => {
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                });
              }}
            >
              {copied ? 'Copied' : 'Copy link'}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

export function Feedback() {
  const qc = useQueryClient();
  const { can } = useStaff();
  const canDelete = can('records.delete');
  const [days, setDays] = useState(90);
  const q = useQuery({
    queryKey: ['feedback', days],
    queryFn: async () => (await api.get<Report>(`/admin/reports/feedback?days=${days}`)).data,
  });
  const [target, setTarget] = useState<Recent | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const r = q.data;
  const latestHead = ['When', 'From', 'Overall', 'By pizza', 'Again?', 'Comment'];
  return (
    <Section title="Feedback">
      <FromTheTeam />
      <h2 style={{ margin: '8px 0 -4px', fontSize: 20 }}>From customers</h2>
      <FeedbackLink />
      <FormGrid min={240} style={{ marginBottom: -8 }}>
        <SelectField
          label="Period"
          value={String(days)}
          onChange={(e) => setDays(Number(e.target.value))}
          options={PERIODS}
        />
      </FormGrid>
      {q.isError && <Notice kind="error">Could not load feedback.</Notice>}
      {msg && <Notice kind="ok">{msg}</Notice>}
      {r && (
        <Card>
          {r.response_rate.completed > 0
            ? `${r.response_rate.rated} of ${r.response_rate.completed} picked-up orders were rated (${Math.round((r.response_rate.rated / r.response_rate.completed) * 100)}%).`
            : 'No picked-up orders in this period yet.'}
        </Card>
      )}
      <Table
        head={[
          'Source',
          'Responses',
          'Overall',
          'Crust',
          'Sauce',
          'Toppings',
          'Order again: yes / maybe / no',
        ]}
        rows={(r?.summary ?? []).map((s) => [
          sourceLabel(s.source),
          String(s.responses),
          score(s.overall),
          score(s.crust),
          score(s.sauce),
          score(s.toppings),
          `${s.yes} / ${s.maybe} / ${s.no}`,
        ])}
        empty="No feedback yet."
      />
      <h3 style={{ margin: 0 }}>By pizza (from orders)</h3>
      <Table
        head={['Pizza', 'Responses', 'Overall']}
        rows={(r?.by_item ?? []).map((i) => [i.name, String(i.responses), score(i.overall)])}
        empty="No order ratings yet."
      />
      <h3 style={{ margin: 0 }}>Latest</h3>
      <Table
        head={canDelete ? [...latestHead, ''] : latestHead}
        wrapColumns={[3, 5]}
        stickyActions={canDelete}
        rowKeys={(r?.recent ?? []).map((f) => f.id)}
        rows={(r?.recent ?? []).map((f) => {
          const cells: ReactNode[] = [
            when(f.created_at),
            fromLabel(f),
            f.overall ? `${f.overall}/5` : '',
            f.pizzas ?? '',
            againLabel(f.would_order),
            f.comment ?? '',
          ];
          if (canDelete)
            cells.push(
              f.can_delete === false ? (
                ''
              ) : (
                <Button
                  variant="danger"
                  aria-label={`Delete feedback from ${fromLabel(f)}`}
                  onClick={() => {
                    setMsg(null);
                    setTarget(f);
                  }}
                >
                  Delete
                </Button>
              ),
            );
          return cells;
        })}
        empty="Nothing yet."
      />
      {target && (
        <ConfirmDelete
          open
          onClose={() => setTarget(null)}
          title="Delete this feedback?"
          description={
            <>
              <span style={{ display: 'block' }}>
                From {fromLabel(target)}, {when(target.created_at)}
                {target.overall ? `, rated ${target.overall}/5` : ''}.
              </span>
              {target.comment && (
                <span style={{ display: 'block', marginTop: 6, fontStyle: 'italic' }}>
                  “
                  {target.comment.length > 160
                    ? `${target.comment.slice(0, 160).trimEnd()}…`
                    : target.comment}
                  ”
                </span>
              )}
              <span style={{ display: 'block', marginTop: 6 }}>
                It also leaves the ratings averages.
              </span>
            </>
          }
          askReason
          onConfirm={async ({ reason }) => {
            await api.delete(withReason(`/admin/feedback/${target.id}`, reason));
            setMsg('Feedback deleted.');
            void qc.invalidateQueries({ queryKey: ['feedback'] });
            void qc.invalidateQueries({ queryKey: ['tastings'] });
            void qc.invalidateQueries({ queryKey: ['tasting'] });
            void qc.invalidateQueries({ queryKey: ['customer'] });
          }}
        />
      )}
    </Section>
  );
}
